import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, CheckCircle, Circle, Play, Clock, BookOpen } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Course = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCourseData();
  }, [courseId]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchCourseData = async () => {
    try {
      const [courseRes, progressRes] = await Promise.all([
        axios.get(`${API}/academy/courses/${courseId}`),
        axios.get(`${API}/academy/progress`, { headers: getAuthHeaders() })
      ]);

      setCourse(courseRes.data);
      const courseProgress = progressRes.data.courses?.find(c => c.course_id === courseId);
      setProgress(courseProgress || { completed_lessons: [], progress: 0 });
    } catch (error) {
      console.error('Error fetching course:', error);
      toast.error('Failed to load course');
    } finally {
      setLoading(false);
    }
  };

  const isLessonCompleted = (lessonId) => {
    return progress?.completed_lessons?.includes(lessonId) || false;
  };

  const startLesson = (lessonId) => {
    navigate(`/academy/lessons/${lessonId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Course not found</h2>
          <Button onClick={() => navigate('/academy')}>Back to Academy</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#050505] via-[#0a0a0f] to-[#050505] text-white">
      {/* Header */}
      <nav className="glass border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/academy')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold">Bethereum Academy</h1>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Course Header */}
        <div className="mb-8">
          <h2 className="text-4xl font-bold mb-4">{course.title}</h2>
          <p className="text-xl text-gray-400 mb-6">{course.description}</p>
          
          <div className="flex items-center gap-4 mb-6">
            <Badge>{course.level}</Badge>
            <span className="text-gray-400">{course.duration}</span>
            <span className="text-gray-400">•</span>
            <span className="text-gray-400">{course.lessons_count} lessons</span>
          </div>

          {/* Progress */}
          <Card className="bg-white/5 border-white/10">
            <CardContent className="pt-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-400">Your Progress</span>
                <span className="text-indigo-400 font-semibold">{Math.round(progress.progress || 0)}%</span>
              </div>
              <Progress value={progress.progress || 0} className="h-3" />
            </CardContent>
          </Card>
        </div>

        {/* Lessons List */}
        <div className="space-y-4">
          <h3 className="text-2xl font-bold mb-4">Lessons</h3>
          {course.lessons?.map((lesson, index) => {
            const completed = isLessonCompleted(lesson.id);
            
            return (
              <Card 
                key={lesson.id}
                className={`bg-white/5 border-white/10 hover:border-indigo-500/50 transition-all cursor-pointer ${
                  completed ? 'border-green-500/30' : ''
                }`}
                onClick={() => startLesson(lesson.id)}
              >
                <CardHeader>
                  <div className="flex items-start gap-4">
                    <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                      completed ? 'bg-green-500/20 text-green-400' : 'bg-indigo-600/20 text-indigo-400'
                    }`}>
                      {completed ? <CheckCircle className="h-5 w-5" /> : <Circle className="h-5 w-5" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg">
                          Lesson {index + 1}: {lesson.title}
                        </CardTitle>
                        {completed && (
                          <Badge className="bg-green-500/20 text-green-300">Completed</Badge>
                        )}
                      </div>
                      <CardDescription className="mt-2">{lesson.description}</CardDescription>
                      <div className="flex items-center gap-3 mt-3 text-sm text-gray-400">
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {lesson.duration || '10 min'}
                        </span>
                        <span>•</span>
                        <span className="flex items-center gap-1">
                          <BookOpen className="h-4 w-4" />
                          {lesson.type || 'Interactive'}
                        </span>
                      </div>
                    </div>
                    <Button variant="ghost" size="icon">
                      <Play className="h-5 w-5" />
                    </Button>
                  </div>
                </CardHeader>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Course;